/**
 * Which claims a player may make on a discard.
 *
 * Pure enumeration only: this module never changes state. The engine validates
 * every submitted claim by matching it against what is enumerated here, so a
 * client, a bot and the server can never disagree about what is legal.
 */

import { getRuleSet } from "./ruleset.js";
import type { RuleSet } from "./ruleset.js";
import { TILE_INFO, kindAtIndex, kindIndex, toKinds } from "./tiles.js";
import type { Tile, TileKind } from "./tiles.js";
import type { ClaimAction, DiscardRef, GameState, PlayerId, PlayerState } from "./types.js";

/** Lowest tile ids first, so enumeration is deterministic. */
function pickCopies(hand: readonly Tile[], kind: TileKind, count: number): Tile[] | null {
  const matches = hand.filter((tile) => tile.kind === kind).sort((a, b) => a.id - b.id);
  return matches.length >= count ? matches.slice(0, count) : null;
}

function kindAtOffset(kind: TileKind, offset: number): TileKind | null {
  const info = TILE_INFO[kind];
  if (info.isHonor) return null;
  const rank = info.rank + offset;
  if (rank < 1 || rank > 9) return null;
  const suitStart = kindIndex(kind) - (info.rank - 1);
  return kindAtIndex(suitStart + rank - 1);
}

/** The player who would draw next: the only seat allowed to chow, by default. */
export function isNextSeat(discarder: PlayerId, claimer: PlayerId): boolean {
  return ((discarder + 1) % 4) === claimer;
}

export function chowOptions(
  player: PlayerState,
  discard: DiscardRef,
  ruleSet: RuleSet,
): ClaimAction[] {
  if (!ruleSet.allowsClaims) return [];
  if (ruleSet.chowFromNextPlayerOnly && !isNextSeat(discard.playerId, player.id)) return [];
  if (TILE_INFO[discard.tile.kind].isHonor) return [];

  const options: ClaimAction[] = [];
  // The three possible positions of the discard inside a run.
  for (const offsets of [
    [-2, -1],
    [-1, 1],
    [1, 2],
  ] as const) {
    const kinds = offsets.map((offset) => kindAtOffset(discard.tile.kind, offset));
    if (kinds.some((kind) => kind === null)) continue;

    const tiles: Tile[] = [];
    let complete = true;
    for (const kind of kinds as TileKind[]) {
      const picked = pickCopies(
        player.hand.filter((tile) => !tiles.some((used) => used.id === tile.id)),
        kind,
        1,
      );
      if (!picked) {
        complete = false;
        break;
      }
      tiles.push(...picked);
    }
    if (!complete) continue;

    options.push({
      type: "chow",
      playerId: player.id,
      tileIds: tiles.map((tile) => tile.id).sort((a, b) => a - b),
    });
  }
  return options;
}

export function pongOptions(
  player: PlayerState,
  discard: DiscardRef,
  ruleSet: RuleSet,
): ClaimAction[] {
  if (!ruleSet.allowsClaims) return [];
  const tiles = pickCopies(player.hand, discard.tile.kind, 2);
  if (!tiles) return [];
  return [
    { type: "pong", playerId: player.id, tileIds: tiles.map((tile) => tile.id) },
  ];
}

export function kongOptions(
  player: PlayerState,
  discard: DiscardRef,
  ruleSet: RuleSet,
): ClaimAction[] {
  if (!ruleSet.allowsClaims) return [];
  if (ruleSet.kongReplacement === "none") return [];
  const tiles = pickCopies(player.hand, discard.tile.kind, 3);
  if (!tiles) return [];
  return [
    { type: "kong", playerId: player.id, tileIds: tiles.map((tile) => tile.id) },
  ];
}

export function ronOptions(
  player: PlayerState,
  discard: DiscardRef,
  ruleSet: RuleSet,
): ClaimAction[] {
  if (!ruleSet.allowsClaims || !ruleSet.allowsWinOnDiscard) return [];
  const kinds = [...toKinds(player.hand), discard.tile.kind];
  if (!ruleSet.analyzeWin(kinds, player.melds.length)) return [];
  return [{ type: "ron", playerId: player.id, tileIds: [] }];
}

/**
 * Every claim `playerId` may make on the tile currently on offer.
 * Ordered by rule-set priority, so a UI can present the strongest first.
 */
export function getClaimOptions(
  state: GameState,
  playerId: PlayerId,
  discard: DiscardRef,
): ClaimAction[] {
  const ruleSet = getRuleSet(state.ruleSetId);
  if (!ruleSet.allowsClaims) return [];
  if (playerId === discard.playerId) return [];

  const player = state.players[playerId];
  return [
    ...ronOptions(player, discard, ruleSet),
    ...kongOptions(player, discard, ruleSet),
    ...pongOptions(player, discard, ruleSet),
    ...chowOptions(player, discard, ruleSet),
  ];
}

/** Kongs a player may declare from their own hand while it is their turn. */
export function selfKongOptions(state: GameState, playerId: PlayerId): ClaimAction[] {
  const ruleSet = getRuleSet(state.ruleSetId);
  if (ruleSet.kongReplacement === "none") return [];
  if (state.deadWall.length === 0) return [];

  const player = state.players[playerId];
  const concealed = player.drawnTile ? [...player.hand, player.drawnTile] : [...player.hand];
  const options: ClaimAction[] = [];

  if (ruleSet.allowsConcealedKong) {
    const seen = new Set<TileKind>();
    for (const tile of concealed) {
      if (seen.has(tile.kind)) continue;
      seen.add(tile.kind);
      const four = pickCopies(concealed, tile.kind, 4);
      if (four) {
        options.push({
          type: "kong",
          playerId,
          tileIds: four.map((item) => item.id).sort((a, b) => a - b),
        });
      }
    }
  }

  if (ruleSet.allowsAddedKong) {
    for (const meld of player.melds) {
      if (meld.kind !== "pong") continue;
      const kind = meld.tiles[0]?.kind;
      if (!kind) continue;
      const extra = pickCopies(concealed, kind, 1);
      if (extra) {
        options.push({ type: "kong", playerId, tileIds: extra.map((tile) => tile.id) });
      }
    }
  }

  return options;
}
